<template>
  <div class="da-ping-container">
    <!-- 顶部标题栏 -->
    <div class="header">
      <div class="header-left">
        <span class="clock">{{ currentTime }}</span>
      </div>
      <div class="header-center">
        <h1 class="title">景区数据驾驶舱</h1>
      </div>
      <div class="header-right">
        <span class="update-time">数据更新：{{ updateTime }}</span>
        <el-button
          type="primary"
          size="small"
          @click="refreshData"
          :loading="loading"
        >
          刷新
        </el-button>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="main-content">
      <!-- 左侧栏 -->
      <div class="left-panel">
        <!-- A. 订单与用户分析 -->
        <div class="panel-card">
          <div class="card-title">订单与用户分析</div>
          <div class="card-content">
            <div class="stat-row">
              <div class="stat-item">
                <div class="stat-value">
                  {{ data.OrderUserAnalysis.TodayOrderCount || "-" }}
                </div>
                <div class="stat-label">今日订单</div>
              </div>
              <div class="stat-item">
                <div class="stat-value">
                  {{ data.OrderUserAnalysis.WeekOrderCount || "-" }}
                </div>
                <div class="stat-label">本周订单</div>
              </div>
            </div>
            <div class="stat-row">
              <div class="stat-item">
                <div class="stat-value local">
                  {{ data.OrderUserAnalysis.LocalPercent || "-" }}
                </div>
                <div class="stat-label">本地用户</div>
              </div>
              <div class="stat-item">
                <div class="stat-value external">
                  {{ data.OrderUserAnalysis.ExternalPercent || "-" }}
                </div>
                <div class="stat-label">外地用户</div>
              </div>
            </div>
          </div>
        </div>

        <!-- B. 用户来源分布 -->
        <div class="panel-card">
          <div class="card-title">用户来源分布</div>
          <div class="card-content chart-container">
            <div ref="userSourceChart" class="chart"></div>
          </div>
        </div>

        <!-- C. 旅游资源分布 -->
        <div class="panel-card">
          <div class="card-title">旅游资源分布</div>
          <div class="card-content chart-container">
            <div ref="resourceTypeChart" class="chart"></div>
          </div>
        </div>
      </div>

      <!-- 中央栏 -->
      <div class="center-panel">
        <!-- D. 景点热度TOP5 -->
        <div class="panel-card full-height">
          <div class="card-title">景点热度 TOP5</div>
          <div class="card-content">
            <div class="scenic-list">
              <div
                v-for="(item, index) in data.ScenicSpotTop5"
                :key="index"
                class="scenic-item"
              >
                <div class="scenic-rank" :class="'rank-' + (index + 1)">
                  {{ index + 1 }}
                </div>
                <div class="scenic-info">
                  <div class="scenic-name">{{ item.Name || "-" }}</div>
                  <div class="scenic-coord" v-if="item.Jd && item.Wd">
                    坐标：{{ item.Jd }}, {{ item.Wd }}
                  </div>
                </div>
                <div class="scenic-sales">{{ item.SalesCount || 0 }} 单</div>
              </div>
              <div
                v-if="!data.ScenicSpotTop5 || data.ScenicSpotTop5.length === 0"
                class="empty-tip"
              >
                暂无数据（需配置景区坐标）
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧栏 -->
      <div class="right-panel">
        <!-- E. 热销产品月度趋势 -->
        <div class="panel-card">
          <div class="card-title">热销产品月度趋势</div>
          <div class="card-content chart-container">
            <div ref="hotProductChart" class="chart"></div>
          </div>
        </div>

        <!-- F. 产品销售排行榜 -->
        <div class="panel-card">
          <div class="card-title">产品销售排行榜</div>
          <div class="card-content">
            <div class="rank-list">
              <div
                v-for="(item, index) in data.ProductSalesRank"
                :key="index"
                class="rank-item"
              >
                <div class="rank-num" :class="'num-' + (index + 1)">
                  {{ index + 1 }}
                </div>
                <div class="rank-name">{{ item.Name || "-" }}</div>
                <div class="rank-bar">
                  <div
                    class="rank-bar-fill"
                    :style="{ width: getBarWidth(item.SalesCount) + '%' }"
                  ></div>
                </div>
                <div class="rank-value">{{ item.SalesCount || 0 }}</div>
              </div>
              <div
                v-if="
                  !data.ProductSalesRank || data.ProductSalesRank.length === 0
                "
                class="empty-tip"
              >
                暂无数据
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, onUnmounted, nextTick } from "vue";
import * as echarts from "echarts";
import $http from "@/http/index";

export default defineComponent({
  name: "DaPing",
  setup() {
    const data = ref({
      OrderUserAnalysis: {},
      UserSourceDistribution: [],
      ResourceTypeDistribution: [],
      ScenicSpotTop5: [],
      HotProductTrend: {
        Months: [],
        MenPiaoSales: [],
        DaoYouSales: [],
        WenChuangSales: [],
      },
      ProductSalesRank: [],
      UpdateTime: "",
    });

    const loading = ref(false);
    const currentTime = ref("");
    const updateTime = ref("");

    const userSourceChart = ref(null);
    const resourceTypeChart = ref(null);
    const hotProductChart = ref(null);

    let userSourceChartInstance = null;
    let resourceTypeChartInstance = null;
    let hotProductChartInstance = null;
    let timer = null;
    let clockTimer = null;

    const updateClock = () => {
      const now = new Date();
      currentTime.value = now.toLocaleString("zh-CN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    };

    const fetchData = async () => {
      try {
        loading.value = true;
        const res = await $http.get("/JingQuDaPing/GetData");
        if (res.data) {
          data.value = res.data;
          updateTime.value = res.data.UpdateTime
            ? new Date(res.data.UpdateTime).toLocaleString("zh-CN")
            : "";
          await nextTick();
          initCharts();
        }
      } catch (error) {
        console.error("获取大屏数据失败:", error);
      } finally {
        loading.value = false;
      }
    };

    const refreshData = async () => {
      try {
        loading.value = true;
        const res = await $http.post("/JingQuDaPing/Refresh");
        if (res.data) {
          data.value = res.data;
          updateTime.value = res.data.UpdateTime
            ? new Date(res.data.UpdateTime).toLocaleString("zh-CN")
            : "";
          await nextTick();
          initCharts();
        }
      } catch (error) {
        console.error("刷新大屏数据失败:", error);
      } finally {
        loading.value = false;
      }
    };

    const getBarWidth = (salesCount) => {
      if (
        !data.value.ProductSalesRank ||
        data.value.ProductSalesRank.length === 0
      )
        return 0;
      const maxSales = Math.max(
        ...data.value.ProductSalesRank.map((item) => item.SalesCount)
      );
      if (maxSales === 0) return 0;
      return (salesCount / maxSales) * 100;
    };

    const initCharts = () => {
      initUserSourceChart();
      initResourceTypeChart();
      initHotProductChart();
    };

    const initUserSourceChart = () => {
      if (!userSourceChart.value) return;
      if (userSourceChartInstance) userSourceChartInstance.dispose();
      userSourceChartInstance = echarts.init(userSourceChart.value);

      const chartData = data.value.UserSourceDistribution || [];
      const dates = chartData.map((item) => item.Date);
      const localData = chartData.map((item) => item.LocalCount);
      const externalData = chartData.map((item) => item.ExternalCount);

      const option = {
        tooltip: { trigger: "axis" },
        legend: {
          data: ["本地", "外地"],
          textStyle: { color: "#fff", fontSize: 10 },
          top: 0,
        },
        grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true },
        xAxis: {
          type: "category",
          data: dates,
          axisLabel: { color: "#fff", fontSize: 10 },
          axisLine: { lineStyle: { color: "#333" } },
        },
        yAxis: {
          type: "value",
          axisLabel: { color: "#fff", fontSize: 10 },
          splitLine: { lineStyle: { color: "#333" } },
        },
        series: [
          {
            name: "本地",
            type: "bar",
            data: localData,
            itemStyle: { color: "#00d4ff" },
          },
          {
            name: "外地",
            type: "bar",
            data: externalData,
            itemStyle: { color: "#ff6b6b" },
          },
        ],
      };

      userSourceChartInstance.setOption(option);
    };

    const initResourceTypeChart = () => {
      if (!resourceTypeChart.value) return;
      if (resourceTypeChartInstance) resourceTypeChartInstance.dispose();
      resourceTypeChartInstance = echarts.init(resourceTypeChart.value);

      const chartData = data.value.ResourceTypeDistribution || [];

      const option = {
        tooltip: { trigger: "item" },
        legend: {
          orient: "vertical",
          right: 10,
          top: "center",
          textStyle: { color: "#fff", fontSize: 10 },
        },
        series: [
          {
            type: "pie",
            radius: ["40%", "70%"],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 5,
              borderColor: "#0a1929",
              borderWidth: 2,
            },
            label: {
              show: true,
              color: "#fff",
              fontSize: 10,
              formatter: "{b}\n{c}",
            },
            data: chartData.map((item, index) => ({
              value: item.Count,
              name: item.Name,
              itemStyle: {
                color: [
                  "#00d4ff",
                  "#ff6b6b",
                  "#ffd93d",
                  "#6bcb77",
                  "#4d96ff",
                  "#ff6b9d",
                ][index % 6],
              },
            })),
          },
        ],
      };

      resourceTypeChartInstance.setOption(option);
    };

    const initHotProductChart = () => {
      if (!hotProductChart.value) return;
      if (hotProductChartInstance) hotProductChartInstance.dispose();
      hotProductChartInstance = echarts.init(hotProductChart.value);

      const trend = data.value.HotProductTrend || {};
      const months = trend.Months || [];
      const menPiao = trend.MenPiaoSales || [];
      const daoYou = trend.DaoYouSales || [];
      const wenChuang = trend.WenChuangSales || [];

      const option = {
        tooltip: { trigger: "axis" },
        legend: {
          data: ["门票", "导览", "文创"],
          textStyle: { color: "#fff", fontSize: 10 },
          top: 0,
        },
        grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true },
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: months,
          axisLabel: { color: "#fff", fontSize: 10 },
          axisLine: { lineStyle: { color: "#333" } },
        },
        yAxis: {
          type: "value",
          axisLabel: { color: "#fff", fontSize: 10 },
          splitLine: { lineStyle: { color: "#333" } },
        },
        series: [
          {
            name: "门票",
            type: "line",
            data: menPiao,
            itemStyle: { color: "#00d4ff" },
            smooth: true,
          },
          {
            name: "导览",
            type: "line",
            data: daoYou,
            itemStyle: { color: "#ffd93d" },
            smooth: true,
          },
          {
            name: "文创",
            type: "line",
            data: wenChuang,
            itemStyle: { color: "#ff6b9d" },
            smooth: true,
          },
        ],
      };

      hotProductChartInstance.setOption(option);
    };

    const handleResize = () => {
      userSourceChartInstance?.resize();
      resourceTypeChartInstance?.resize();
      hotProductChartInstance?.resize();
    };

    onMounted(() => {
      updateClock();
      clockTimer = setInterval(updateClock, 1000);
      fetchData();
      timer = setInterval(fetchData, 30000);
      window.addEventListener("resize", handleResize);
    });

    onUnmounted(() => {
      if (timer) clearInterval(timer);
      if (clockTimer) clearInterval(clockTimer);
      window.removeEventListener("resize", handleResize);
      userSourceChartInstance?.dispose();
      resourceTypeChartInstance?.dispose();
      hotProductChartInstance?.dispose();
    });

    return {
      data,
      loading,
      currentTime,
      updateTime,
      userSourceChart,
      resourceTypeChart,
      hotProductChart,
      refreshData,
      getBarWidth,
    };
  },
});
</script>

<style scoped>
.da-ping-container {
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #0a1929 0%, #1a2942 50%, #0a1929 100%);
  color: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.header {
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(0, 212, 255, 0.3);
}

.header-left {
  flex: 1;
}

.header-center {
  flex: 2;
  text-align: center;
}

.header-right {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 15px;
}

.title {
  font-size: 24px;
  font-weight: bold;
  margin: 0;
  background: linear-gradient(90deg, #00d4ff, #00ff88);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.clock,
.update-time {
  font-size: 14px;
  color: #00d4ff;
}

.main-content {
  flex: 1;
  display: grid;
  grid-template-columns: 22vw 1fr 22vw;
  gap: 15px;
  padding: 15px;
  overflow: hidden;
}

.left-panel,
.right-panel {
  display: flex;
  flex-direction: column;
  gap: 15px;
  overflow: hidden;
}

.center-panel {
  overflow: hidden;
}

.panel-card {
  background: rgba(10, 25, 41, 0.8);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.left-panel .panel-card,
.right-panel .panel-card {
  flex: 1;
}

.center-panel .panel-card {
  height: 100%;
}

.card-title {
  padding: 12px 15px;
  font-size: 16px;
  font-weight: bold;
  color: #00d4ff;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  background: rgba(0, 212, 255, 0.05);
}

.card-content {
  padding: 15px;
  flex: 1;
  overflow: auto;
}

.chart-container {
  padding: 10px;
}

.chart {
  width: 100%;
  height: 100%;
  min-height: 200px;
}

.stat-row {
  display: flex;
  justify-content: space-around;
  margin-bottom: 15px;
}

.stat-item {
  text-align: center;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
  color: #fff;
}

.stat-value.local {
  color: #00d4ff;
}

.stat-value.external {
  color: #ff6b6b;
}

.stat-label {
  font-size: 12px;
  color: #888;
  margin-top: 5px;
}

.scenic-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.scenic-item {
  display: flex;
  align-items: center;
  padding: 10px;
  background: rgba(0, 212, 255, 0.05);
  border-radius: 5px;
  border: 1px solid rgba(0, 212, 255, 0.1);
}

.scenic-rank {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 15px;
  background: rgba(255, 255, 255, 0.1);
}

.scenic-rank.rank-1 {
  background: #ffd93d;
  color: #000;
}

.scenic-rank.rank-2 {
  background: #c0c0c0;
  color: #000;
}

.scenic-rank.rank-3 {
  background: #cd7f32;
  color: #fff;
}

.scenic-info {
  flex: 1;
}

.scenic-name {
  font-size: 14px;
  font-weight: bold;
}

.scenic-coord {
  font-size: 11px;
  color: #888;
  margin-top: 3px;
}

.scenic-sales {
  font-size: 14px;
  color: #00d4ff;
  font-weight: bold;
}

.rank-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.rank-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.rank-num {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  background: rgba(255, 255, 255, 0.1);
}

.rank-num.num-1 {
  background: #ffd93d;
  color: #000;
}

.rank-num.num-2 {
  background: #c0c0c0;
  color: #000;
}

.rank-num.num-3 {
  background: #cd7f32;
  color: #fff;
}

.rank-name {
  width: 100px;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rank-bar {
  flex: 1;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.rank-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #00d4ff, #00ff88);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.rank-value {
  width: 40px;
  text-align: right;
  font-size: 13px;
  color: #00d4ff;
}

.empty-tip {
  text-align: center;
  color: #888;
  padding: 20px;
}

/* 响应式适配 */
@media (max-aspect-ratio: 4/3) {
  .main-content {
    grid-template-columns: 25vw 1fr 25vw;
  }
  .title {
    font-size: 20px;
  }
  .stat-value {
    font-size: 24px;
  }
}

@media (min-aspect-ratio: 21/9) {
  .main-content {
    grid-template-columns: 20vw 1fr 20vw;
  }
  .title {
    font-size: 28px;
  }
  .stat-value {
    font-size: 32px;
  }
}
</style>
